import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { injectStripe } from 'react-stripe-elements';
import * as reducers from 'Store/reducers';
import {
  enableFormReconciliationMode,
  submitForm,
  setFormMessage,
  clearFormMessage,
} from 'Store/forms/actions';
import { setAuthToken, fetchActiveUser } from 'Store/users/actions';
import { createNewSchoolWithSubscription, postLogin } from '../../api';
import { setTokenCookie } from '../../utilities';

import Form from 'Components/Form';
import PaymentLoading from 'Components/PaymentLoading/PaymentLoading';
import { ROUTES } from 'Settings/route-strings';
import ConnectedFormattedMessage from 'Containers/ConnectedFormattedMessage';

const FORM_ID = 'subscriptionPurchase';

class NewSchoolSubscriptionPurchaseForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isPosting: false,
      showBillingFields: false,
      waitingForAuthToken: false,
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.navigateToConfirmation = this.navigateToConfirmation.bind(this);
    this.loginUser = this.loginUser.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { activeUser } = this.props;
    /* Navigate to confirmation screen when the user is logged in and fetched */
    if (activeUser !== null && prevProps.activeUser === null) {
      this.navigateToConfirmation();
    }
  }

  navigateToConfirmation() {
    const { history } = this.props;
    history.push({
      pathname: ROUTES.SETTINGS_SUBSCRIPTION_CONFIRMATION,
      state: { justSubscribed: true, needsAuthToken: true },
    });
  }

  loginUser() {
    const { user } = this.props;
    if (!user) return;
    postLogin({
      username: { value: user.auth_info.username },
      password: { value: user.auth_info.password },
    }).then((response) => {
      const { token } = response.results;
      this.props.setAuthToken(token);
      setTokenCookie(token);
      /* fetch the active user once we have logged in */
      this.props.fetchActiveUser();
    });
  }

  handleSubmit(fields, isFormValid) {
    const { user, authToken, plan, school, stripe } = this.props;

    this.props.clearFormMessage(FORM_ID, FORM_ID);
    if (!isFormValid) {
      this.props.enableFormReconciliationMode(FORM_ID, FORM_ID);
      return;
    }

    this.setState({ isPosting: true });

    stripe
      .createSource({
        type: 'card',
        owner: {
          name: fields.subscriptionPurchase__cardholders_name.value,
        },
      })
      .then((source) => {
        const selectedPlan = plan.availableUpgrade || plan.licenseId;
        return createNewSchoolWithSubscription(
          source.source.id,
          authToken,
          school,
          user,
          selectedPlan,
          fields
        );
      })
      .then((result) => {
        const { errorCode, status, meta } = result;
        if (errorCode || (status && status >= 500) || meta.status >= 400) {
          this.setState({ isPosting: false });
          throw result;
        } else {
          this.setState({ isPosting: true, waitingForAuthToken: true });
          this.loginUser();
        }
      })
      .catch((e) => {
        this.props.enableFormReconciliationMode(FORM_ID, FORM_ID);
        this.props.setFormMessage(FORM_ID, FORM_ID, {
          text: e.message || e.errorMsg || 'Error!',
          type: 'error',
          animationType: 'fade',
        });
        this.setState({ isPosting: false });
      });
  }

  handleBillingClick(e) {
    e.preventDefault();
    this.setState({ showBillingFields: true });
  }

  render() {
    const { isPosting, showBillingFields, waitingForAuthToken } = this.state;
    const { plan, user } = this.props;

    const visibilityComponent = (
      <div className="Form__visibility-link">
        If you’d like,{' '}
        <button
          type="button"
          className="u-text-link"
          href="#"
          onClick={(e) => this.handleBillingClick(e)}
        >
          add billing contact information
        </button>
        .
      </div>
    );

    return (
      <React.Fragment>
        {isPosting && (
          <PaymentLoading waitingForAuthToken={waitingForAuthToken} />
        )}
        <Form
          type={FORM_ID}
          id={FORM_ID}
          preSubmitNote={
            <div>
              <ConnectedFormattedMessage id="payment_form.pre_submit_message" />
            </div>
          }
          messagePosition="content-frame"
          submitButtonType="secondary"
          submitButtonLabelValues={{ price: `$${plan.computedPrice}` }}
          formStyle="full-width"
          submitHandler={this.handleSubmit}
          isReadOnly={isPosting}
          initialFieldSettings={{
            cardholders_name: {
              value: `${user.given_name} ${user.family_name}`,
            },
          }}
          groupVisibility={{
            'billing-contact': {
              visible: showBillingFields,
              visibilityComponent,
            },
          }}
        />
      </React.Fragment>
    );
  }
}

NewSchoolSubscriptionPurchaseForm.propTypes = {
  user: PropTypes.object.isRequired,
  plan: PropTypes.object.isRequired,
  school: PropTypes.object.isRequired,
  authToken: PropTypes.string,
  activeUser: PropTypes.object,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  setAuthToken: PropTypes.func.isRequired,
  enableFormReconciliationMode: PropTypes.func.isRequired,
  setFormMessage: PropTypes.func.isRequired,
  clearFormMessage: PropTypes.func.isRequired,
  fetchActiveUser: PropTypes.func.isRequired,
  stripe: PropTypes.shape({
    createSource: PropTypes.func.isRequired,
  }).isRequired,
};

const mapStateToProps = (state, { school }) => {
  return {
    authToken: reducers.getAuthToken(state),
    activeUser: reducers.getActiveUser(state),
    school: school || reducers.getActiveSchool(state),
  };
};

const NewSchoolSubscriptionPurchaseFormWithStripe = withRouter(
  injectStripe(NewSchoolSubscriptionPurchaseForm)
);

export default connect(mapStateToProps, {
  enableFormReconciliationMode,
  submitForm,
  setFormMessage,
  clearFormMessage,
  setAuthToken,
  fetchActiveUser,
})(NewSchoolSubscriptionPurchaseFormWithStripe);
